import React, { useEffect, useState } from 'react';
import { getIncidentTypes, getIncidentActions, updateOrder, getOrderById, logError } from '../api';
import { useNavigate } from 'react-router-dom';
import EditItemsInOrder from './Global/EditItemsInOrder';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { Form } from 'react-bootstrap';

const IncidentForm = ({ orderId, initialMileage }) => {
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [incidentActions, setIncidentActions] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mileage, setMileage] = useState(initialMileage || 0);
  const [order, setOrder] = useState(null);
  const [specialProducts, setSpecialProducts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchIncidentData = async () => {
      setLoading(true);
      try {
        const types = await getIncidentTypes();
        setIncidentTypes(types);
        const actions = await getIncidentActions();
        setIncidentActions(actions);
      } catch (err) {
        logError(err);
        console.error('Error al cargar datos de incidencia:', err);
        setError('No se pudieron cargar los datos de incidencia.');
      } finally {
        setLoading(false);
      }
    };

    const fetchOrderItems = async () => {
      try {
        // Obtener la orden completa
        const orderData = await getOrderById(orderId);
        setOrder(orderData);
        
        // Usar los items y productos especiales de la orden
        setOrderItems(orderData.items || []);
        setSpecialProducts(orderData.specialProducts || []);
      } catch (err) {
        logError(err);
        console.error('Error al cargar los artículos del pedido:', err);
        setError('No se pudieron cargar los artículos del pedido.');
      }
    };

    fetchIncidentData();
    fetchOrderItems();
  }, [orderId]);

  const handleSaveIncident = async (updatedItems, updatedSpecialProducts, newMileage) => {
    if (!selectedType || !selectedAction) {
      alert('Debe seleccionar un tipo y una acción de incidencia.');
      return;
    }

    const processedUpdatedItems = updatedItems.map((item) => ({
      ...item,
      id: String(item.id).startsWith('new-') ? 0 : item.id,
      // Asegurar que productPrice incluye toda la información necesaria
      productPrice: {
        ...item.productPrice,
        category: item.productPrice?.category || item.productPrice?.type?.category,
        categoryId: item.productPrice?.categoryId || item.productPrice?.type?.categoryId
      }
    }));
    
    // Procesar productos especiales sin updatedAt
    const processedSpecialProducts = updatedSpecialProducts.map(item => {
      // Eliminar updatedAt si existe
      const { updatedAt, ...itemWithoutUpdatedAt } = item;
      return {
        ...itemWithoutUpdatedAt,
        id: item.id < 0 ? 0 : item.id,
        orderId
      };
    });

    const incidentData = {
      id: orderId,
      IncidentTypeId: selectedType,
      IncidentActionId: selectedAction,
      HasIncident: true,
      NewDeliveryDate: selectedAction === '1' ? selectedDate : null,
      Paid: Number(paymentAmount) || null,
      // Solo incluir ModifiedItems y ModifiedSpecialProducts si la acción es cambios de producto (ID 4)
      ...(selectedAction === '4' ? { 
        ModifiedItems: processedUpdatedItems,
        ModifiedSpecialProducts: processedSpecialProducts
      } : {}),
    };
    
    // Incluir mileage solo si la acción es cambios de producto (ID 4) y mileage ha sido editado
    if (selectedAction === '4' && newMileage !== null && newMileage !== undefined && newMileage !== initialMileage) {
      incidentData.mileage = newMileage;
    }

    try {
      await updateOrder(orderId, incidentData);
      if (window.location.pathname.endsWith('/orders')) {
        window.location.reload();
      } else {
        navigate(-1);
      }

    } catch (err) {
      logError(err);
      console.error('Error al guardar la incidencia:', err);
      alert('Hubo un error al guardar la incidencia.');
    }
  };

  const handleItemsChange = (updatedItems, updatedSpecialProducts) => {
    setOrderItems(updatedItems);
    setSpecialProducts(updatedSpecialProducts || []);
  };

  const handleOrderMileageChange = (newMileage) => {
    setMileage(newMileage);
  };

  if (loading) return <div>Cargando datos de incidencia...</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  const isSaveDisabled =
    !selectedType || !selectedAction || (selectedAction === '1' && !selectedDate);

  return (
    <div className="mt-4 p-3 border rounded bg-light">
      <h3>Incidencias</h3>

      <div className="form-group">
        <label htmlFor="incidentType">Tipo de Incidencia</label>
        <select
          id="incidentType"
          className="form-control"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          data-testid="incident-type-select"
        >
          <option value="">Seleccione un tipo</option>
          {incidentTypes.map((type) => (
            <option key={type.valueId} value={type.valueId}>
              {type.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="incidentAction">Acción sobre la Incidencia</label>
        <select
          id="incidentAction"
          className="form-control"
          value={selectedAction}
          onChange={(e) => setSelectedAction(e.target.value)}
          data-testid="incident-action-select"
        >
          <option value="">Seleccione una acción</option>
          {incidentActions.map((action) => (
            <option key={action.valueId} value={action.valueId}>
              {action.name}
            </option>
          ))}
        </select>
      </div>

      {selectedAction !== '3' && (
        <div className="form-group">
          <label htmlFor="paymentAmount">Pago Realizado (opcional)</label>
          <input
            type="number"
            id="paymentAmount"
            className="form-control"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            placeholder="Ingrese la cantidad pagada"
            data-testid="payment-amount-input"
          />
        </div>
      )}

      {selectedAction === '1' && (
        <div className="form-group">
          <Form.Group controlId="newDeliveryDate">
            <Form.Label>Nueva Fecha de Entrega</Form.Label>
            <DatePicker
                selected={selectedDate} 
                onChange={date => setSelectedDate(format(date, 'yyyy-MM-dd'))}
                dateFormat="dd/MM/yyyy"
                placeholderText="Seleccionar fecha"
                className="form-control"
                todayButton="Hoy"
                showPopperArrow={false}
                data-testid="new-delivery-date-picker"
            />
          </Form.Group>
        </div>
      )}

      {selectedAction === '4' && (
        <EditItemsInOrder
          initialItems={orderItems}
          initialSpecialProducts={specialProducts}
          onItemsChange={handleItemsChange}
          onOrderMileageChange={handleOrderMileageChange}
          initialMileage={initialMileage}
          onSave={async (items, specialProducts, mileage) => {
            await handleSaveIncident(items, specialProducts, mileage);
          }}
          saveLabel="Guardar Incidencia"
        />
      )}

      {selectedAction !== '4' && (
        <div className="d-flex justify-content-end mt-3">
          <button
            className="btn btn-primary"
            onClick={() => handleSaveIncident(orderItems, specialProducts, mileage)}
            disabled={isSaveDisabled}
            data-testid="save-incident-button"
          >
            Guardar Incidencia
          </button>
        </div>
      )}

    </div>
  );
};

export default IncidentForm;
